import {
    ComponentProps,
    MouseEventHandler,
    RefObject,
    useEffect,
    useState,
} from 'react'
import classnames from 'classnames'

import { Button } from '@atoms'

import { FilterButton } from '../Filter/Filter/FilterButton'
import { LinkButton } from '../LinkButton/LinkButton'
import { IconButton } from '../IconButton/IconButton'
import {
    useFloating,
    autoUpdate,
    flip,
    offset,
    size,
    useMergeRefs,
} from '@floating-ui/react'
import { createPortal } from 'react-dom'
import { useDropdownZIndexStore } from '@/stores/useDropdownZIndexStore/useDropdownZIndexStore'

// Define the valid button types as a union type
type ButtonComponentType =
    | typeof LinkButton
    | typeof IconButton
    | typeof Button
    | typeof FilterButton

export type DropdownProps<T extends ButtonComponentType = ButtonComponentType> =
    {
        name: string
        buttonProps: ComponentProps<T>
        buttonComponent: T
        children: React.ReactNode
        dropdownOffset?: number
        horizontalAlign?: 'left' | 'right'
        verticalAlign?: 'top' | 'bottom'
        isOpen: boolean
        isError?: boolean
        onClick?: MouseEventHandler
        dropdownRef?: RefObject<HTMLDivElement>
        dropdownClassname?: string
        wrapperClassname?: string
        buttonWrapperClassname?: string
        matchReferenceWidth?: boolean
    }

export const Dropdown = <T extends ButtonComponentType>(
    props: DropdownProps<T>
) => {
    const {
        buttonProps,
        buttonComponent: ButtonComponent,
        children,
        dropdownOffset = 4,
        horizontalAlign = 'left',
        verticalAlign = 'bottom',
        dropdownRef,
        isOpen,
        onClick,
        name,
        matchReferenceWidth,
    } = props

    const [zIndex, setZIndex] = useState(1000)

    // needed to ensure that dropdowns inside other dropdowns are rendered correctly
    // and not hidden behind the parent dropdown
    // this is done by using a store that manages the z-index of dropdowns
    useEffect(() => {
        if (isOpen) {
            setZIndex(useDropdownZIndexStore.getState().getNext())
        }
    }, [isOpen])

    const dropdownClassname = classnames(
        'dropdown-content rounded-xl border border-neutral-200 bg-white shadow-md',
        'absolute z-10',
        {
            hidden: !isOpen,
        },
        props.dropdownClassname
    )

    const wrapperClassname = classnames('flex', props.wrapperClassname)

    const buttonWrapperClassname = classnames(
        'text-label-small',
        props.buttonWrapperClassname
    )

    const side = verticalAlign
    const alignment = horizontalAlign === 'left' ? 'start' : 'end' // map 'left' -> 'start', 'right' -> 'end'
    const placement = `${side}-${alignment}` as const // 'bottom-start'

    const middleware = [offset(dropdownOffset), flip()]

    if (matchReferenceWidth) {
        middleware.push(
            size({
                apply({ rects, elements }) {
                    elements.floating.style.width = `${rects.reference.width.toString()}px`
                },
            })
        )
    }

    const { refs, floatingStyles } = useFloating({
        placement: placement,
        whileElementsMounted: autoUpdate,
        open: isOpen,
        middleware,
    })

    const mergedFloatingRef = useMergeRefs([refs.setFloating, dropdownRef])

    const styles = {
        ...floatingStyles,
        zIndex: zIndex,
    }

    return (
        <div id={name} data-testid={name} className={wrapperClassname}>
            <div className={buttonWrapperClassname} ref={refs.setReference}>
                {ButtonComponent === Button && (
                    <Button
                        {...(buttonProps as ComponentProps<typeof Button>)}
                        onClick={onClick}
                    />
                )}
                {ButtonComponent === IconButton && (
                    <IconButton
                        {...(buttonProps as ComponentProps<typeof IconButton>)}
                        onClick={onClick}
                    />
                )}
                {ButtonComponent === LinkButton && (
                    <LinkButton
                        {...(buttonProps as ComponentProps<typeof LinkButton>)}
                        onClick={onClick}
                    />
                )}
                {ButtonComponent === FilterButton && (
                    <FilterButton
                        {...(buttonProps as ComponentProps<
                            typeof FilterButton
                        >)}
                        onClick={onClick}
                        isError={props.isError}
                    />
                )}
            </div>

            {createPortal(
                <div
                    className={dropdownClassname}
                    data-testid={`${name}-dropdown-content`}
                    ref={mergedFloatingRef}
                    style={styles}
                >
                    {children}
                </div>,
                document.body
            )}
        </div>
    )
}
